/*
 * Floating Video Story (vh-fvs) — controlador isolado.
 *
 * NÃO compartilha estado com vh-stories.js. Responsável por:
 *  - arrastar a bolha (Pointer Events: cobre mouse + touch)
 *  - distinguir clique de drag (threshold em px) pra não abrir modal ao soltar
 *  - prender a bolha dentro da viewport (clamp) e reposicionar no resize
 *  - no mobile na seção, arrastar sem virar fixed
 *  - lembrar a posição na sessão (sessionStorage)
 *  - abrir/fechar o modal/player 9:16 (portal pro <body>)
 */
(function () {
  if (window.__vhFvsLoaded) return;
  window.__vhFvsLoaded = true;

  var DRAG_THRESHOLD = 6; // px de movimento que caracteriza drag (não clique)
  var EDGE = 12; // margem mínima da borda da viewport

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function init(root) {
    if (!root || root.dataset.vhFvsInit === '1') return;
    root.dataset.vhFvsInit = '1';

    var bubble = root.querySelector('[data-vh-fvs-trigger]');
    var modal = root.querySelector('[data-vh-fvs-modal]') ||
                document.querySelector('[data-vh-fvs-modal]');
    // O modal é irmão do root no snippet; pega o mais próximo do mesmo grupo.
    var siblingModal = root.nextElementSibling;
    if (siblingModal && siblingModal.matches && siblingModal.matches('[data-vh-fvs-modal]')) {
      modal = siblingModal;
    }
    if (!bubble || !modal) return;

    var video = modal.querySelector('[data-vh-fvs-video]');
    var closeBtns = modal.querySelectorAll('[data-vh-fvs-close]');
    if (!video) return;

    // Portal do modal pro <body>: escapa stacking contexts (info column,
    // sticky, etc.) que prenderiam o position:fixed sob o header.
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    var storageKey = root.getAttribute('data-vh-fvs-storage-key') || '';
    var isOpen = false;
    var mobileMql = window.matchMedia('(max-width: 749px)');

    /* ---------- posicionamento ---------- */
    function isInlineMobile() {
      return root.getAttribute('data-vh-fvs-mobile-inline') === 'true' && mobileMql.matches;
    }

    function getSize() {
      var rect = root.getBoundingClientRect();
      return { w: rect.width, h: rect.height };
    }

    function applyPosition(left, top) {
      var size = getSize();
      var maxLeft = window.innerWidth - size.w - EDGE;
      var maxTop = window.innerHeight - size.h - EDGE;
      left = clamp(left, EDGE, Math.max(EDGE, maxLeft));
      top = clamp(top, EDGE, Math.max(EDGE, maxTop));
      // Assume controle total via left/top, neutralizando os presets CSS.
      root.style.left = left + 'px';
      root.style.top = top + 'px';
      root.style.right = 'auto';
      root.style.bottom = 'auto';
      return { left: left, top: top };
    }

    function getMobileOffsetX() {
      var value = root.style.getPropertyValue('--fvs-mobile-offset-x') || '0px';
      var parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    function getMobileOffsetY() {
      var value = root.style.getPropertyValue('--fvs-mobile-offset-y') || '0px';
      var parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    function applyMobileOffset(offsetX, offsetY, originRect) {
      var rect = originRect || root.getBoundingClientRect();
      var currentOffset = getMobileOffsetX();
      var currentOffsetY = getMobileOffsetY();
      var neutralLeft = rect.left - currentOffset;
      var neutralRight = rect.right - currentOffset;
      var neutralTop = rect.top - currentOffsetY;
      var neutralBottom = rect.bottom - currentOffsetY;
      var minOffset = EDGE - neutralLeft;
      var maxOffset = window.innerWidth - EDGE - neutralRight;
      var minOffsetY = EDGE - neutralTop;
      var maxOffsetY = window.innerHeight - EDGE - neutralBottom;
      // Quando a seção está rolada, os limites de viewport podem se inverter
      // (min > max). Sem isso, o clamp forçaria a bolha pra um ponto preso ao
      // scroll. Mantém o offset atual nesse caso (não reposiciona à força).
      if (maxOffset < minOffset) {
        offsetX = getMobileOffsetX();
      } else {
        offsetX = clamp(offsetX, minOffset, maxOffset);
      }
      if (maxOffsetY < minOffsetY) {
        offsetY = getMobileOffsetY();
      } else {
        offsetY = clamp(offsetY, minOffsetY, maxOffsetY);
      }
      root.style.setProperty('--fvs-mobile-offset-x', offsetX + 'px');
      root.style.setProperty('--fvs-mobile-offset-y', offsetY + 'px');
      root.style.left = '';
      root.style.top = '';
      root.style.right = '';
      root.style.bottom = '';
      return { mobileOffsetX: offsetX, mobileOffsetY: offsetY };
    }

    function savePosition(pos) {
      if (!storageKey) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(pos));
      } catch (e) {}
    }

    function restorePosition() {
      if (!storageKey) return false;
      try {
        var raw = sessionStorage.getItem(storageKey);
        if (!raw) return false;
        var pos = JSON.parse(raw);
        if (isInlineMobile()) {
          if (typeof pos.mobileOffsetX === 'number') {
            applyMobileOffset(pos.mobileOffsetX, pos.mobileOffsetY || 0);
            return true;
          }
          return false;
        }
        if (typeof pos.left === 'number' && typeof pos.top === 'number') {
          applyPosition(pos.left, pos.top);
          return true;
        }
      } catch (e) {}
      return false;
    }

    // Se não há posição salva, converte o preset CSS atual em left/top
    // (pra que o clamp no resize sempre tenha coordenadas absolutas).
    function seedFromPreset() {
      if (isInlineMobile()) {
        applyMobileOffset(getMobileOffsetX(), getMobileOffsetY());
        return;
      }
      var rect = root.getBoundingClientRect();
      applyPosition(rect.left, rect.top);
    }

    /* ---------- drag (Pointer Events) ---------- */
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startY = 0;
    var originLeft = 0;
    var originTop = 0;
    var originMobileOffsetX = 0;
    var originMobileOffsetY = 0;
    var originMobileRect = null;

    function onPointerDown(e) {
      // só botão primário do mouse
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startY = e.clientY;
      var rect = root.getBoundingClientRect();
      originLeft = rect.left;
      originTop = rect.top;
      originMobileOffsetX = getMobileOffsetX();
      originMobileOffsetY = getMobileOffsetY();
      originMobileRect = rect;
      root.classList.add('vh-fvs--dragging');
      try { bubble.setPointerCapture(e.pointerId); } catch (err) {}
    }

    function onPointerMove(e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (!moved && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) {
        moved = true;
      }
      if (moved) {
        e.preventDefault();
        if (isInlineMobile()) {
          applyMobileOffset(originMobileOffsetX + dx, originMobileOffsetY + dy, originMobileRect);
        } else {
          applyPosition(originLeft + dx, originTop + dy);
        }
      }
    }

    function onPointerUp(e) {
      if (!dragging) return;
      dragging = false;
      root.classList.remove('vh-fvs--dragging');
      try { bubble.releasePointerCapture(e.pointerId); } catch (err) {}

      if (moved) {
        // foi drag → salva posição e NÃO abre o modal
        if (isInlineMobile()) {
          savePosition({ mobileOffsetX: getMobileOffsetX(), mobileOffsetY: getMobileOffsetY() });
        } else {
          var rect = root.getBoundingClientRect();
          savePosition({ left: rect.left, top: rect.top });
        }
      } else {
        // foi clique → abre o player
        openModal();
      }
    }

    bubble.addEventListener('pointerdown', onPointerDown);
    bubble.addEventListener('pointermove', onPointerMove);
    bubble.addEventListener('pointerup', onPointerUp);
    bubble.addEventListener('pointercancel', function () {
      dragging = false;
      root.classList.remove('vh-fvs--dragging');
    });

    // Teclado: Enter/Espaço abrem (botão nativo já dispara click, mas o
    // click sintético do teclado não passa pelo pointer flow acima).
    bubble.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openModal();
      }
    });

    /* ---------- modal ---------- */
    function openModal() {
      var url = bubble.getAttribute('data-vh-fvs-video-url');
      if (!url) return;
      var source = video.querySelector('source');
      if (source) source.setAttribute('src', url);
      video.setAttribute('src', url);
      try { video.load(); } catch (e) {}
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      isOpen = true;
      video.muted = false;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          video.muted = true;
          video.play().catch(function () {});
        });
      }
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      try { video.pause(); } catch (e) {}
      isOpen = false;
      document.body.style.overflow = '';
    }

    closeBtns.forEach(function (b) {
      b.addEventListener('click', closeModal);
    });
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (isOpen && e.key === 'Escape') closeModal();
    });

    /* ---------- init posição + resize ---------- */
    if (!restorePosition()) {
      seedFromPreset();
    }

    var resizeRaf = null;
    var lastViewportW = window.innerWidth;
    window.addEventListener('resize', function () {
      var widthChanged = window.innerWidth !== lastViewportW;
      lastViewportW = window.innerWidth;
      // No mobile inline, rolar a página mostra/esconde a barra de endereço,
      // disparando "resize" só na altura. Reposicionar nesses casos com a
      // matemática de viewport (elemento é position:absolute) faz a bolha
      // pular pro meio da página. Só reposiciona em mudança real de largura.
      if (isInlineMobile() && !widthChanged) return;
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        if (isInlineMobile()) {
          applyMobileOffset(getMobileOffsetX(), getMobileOffsetY());
        } else {
          var rect = root.getBoundingClientRect();
          applyPosition(rect.left, rect.top);
        }
      });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-vh-fvs]').forEach(init);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-vh-fvs]').forEach(init);
  });
})();
